import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  RouterOutlet,
} from "@angular/router";

import { filter, map, tap } from "rxjs/operators";
import { AuthStateService } from "./core/services/auth-state.service";
import { BackButtonStateService } from "./core/services/back-button-state.service";
import { ContainerComponent } from "./layout/container/container.component";

@Component({
  selector: "szz-app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [
    ContainerComponent,
    RouterOutlet,
],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly backButtonStateService = inject(BackButtonStateService);
  protected readonly authState = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  // Nothing is shown until the first navigation succeeds: the auth guard cancels navigation while it
  // redirects to login. The header and sidebar need a session the guard confirmed as well — the
  // unguarded /error page otherwise gave an anonymous visitor the logged-in layout (F124).
  protected readonly navigated = signal(false);

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap(() => this.navigated.set(true)),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child?.snapshot?.data["showBack"]) {
              return child.snapshot.data["showBack"];
            } else {
              return null;
            }
          }
          return null;
        }),
        tap((x) => {
          this.backButtonStateService.updateButtonState(x || false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
